/**
 * Minimal, non-blocking analytics consent bar.
 * Rendered only when no privacy signal (DNT/GPC) is present and no decision
 * has been stored yet. Declining is as easy as accepting.
 */
import { useEffect, useState } from "react";
import { useI18n } from "@/lib/i18n";
import { setConsent, shouldPromptForConsent } from "@/lib/consent";

export function AnalyticsConsentBar() {
  const { t } = useI18n();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    setVisible(shouldPromptForConsent());
  }, []);

  if (!visible) return null;

  const decide = (state: "granted" | "denied") => {
    setConsent(state);
    setVisible(false);
  };

  return (
    <div
      role="region"
      aria-label={t("consent.title")}
      data-testid="analytics-consent"
      className="pointer-events-none fixed inset-x-0 bottom-0 z-[80] p-3 sm:p-4"
    >
      <div className="pointer-events-auto mx-auto grid w-full max-w-3xl gap-3 rounded-2xl border border-white/10 bg-obsidian/95 p-4 shadow-2xl backdrop-blur sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center">
        <p className="min-w-0 text-xs leading-relaxed text-muted-foreground">
          <span className="font-semibold text-foreground">{t("consent.title")}</span>{" "}
          {t("consent.desc")}
        </p>
        <div className="flex shrink-0 flex-wrap gap-2">
          <button
            type="button"
            onClick={() => decide("denied")}
            className="min-h-11 rounded-xl border border-white/12 px-4 text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground transition hover:bg-white/[0.06] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-saffron/60"
          >
            {t("consent.decline")}
          </button>
          <button
            type="button"
            onClick={() => decide("granted")}
            className="min-h-11 rounded-xl px-4 text-xs font-bold uppercase tracking-[0.14em] text-obsidian transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-saffron"
            style={{ background: "linear-gradient(135deg,#FFD66B,#DEFF9A)" }}
          >
            {t("consent.accept")}
          </button>
        </div>
      </div>
    </div>
  );
}
